import { type FunctionComponent } from 'react';
import { Box, Flex, Stack, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';

import Headline from '../shared/agent-consent/Headline';
import A2ADetails from './A2ADetails/A2ADetails';
import SiteSelector from './SiteSelector/SiteSelector';
import A2APermission from './A2APermission/A2APermission';
import LegalDisclaimer from './LegalDisclaimer/LegalDisclaimer';
import A2AContextProvider, { useA2AContext, A2A_DOMAIN_STATUS } from './A2AContextProvider';
import A2APolicyAlerts from './A2APolicyAlerts/A2APolicyAlerts';
import SiteBypassBanner from '../shared/agent-consent/SiteBypassBanner/SiteBypassBanner';

const rootStyles = xcss({
  width: '100%',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  paddingInline: 'space.200',
  paddingBlock: 'space.400',
});

const spinnerWrapStyles = xcss({
  paddingBlock: 'space.400',
});

/** Reserves roughly the same height as the permission section to prevent layout shift. */
const placeholderStyles = xcss({
  minHeight: '120px',
});

/** Inner content — exported for stories that provide their own A2AContext. */
export const A2AContent: FunctionComponent = () => {
  const { a2aDomainStatus } = useA2AContext();

  return (
    <Box xcss={rootStyles}>
      <Stack space="space.500">
        <Headline />
        <A2ADetails />
        <SiteBypassBanner />
        <SiteSelector />
        {a2aDomainStatus === A2A_DOMAIN_STATUS.loading && (
          <Flex xcss={spinnerWrapStyles} justifyContent="center">
            <Spinner size="large" label="Checking permissions…" />
          </Flex>
        )}
        {a2aDomainStatus === A2A_DOMAIN_STATUS.idle && <Box xcss={placeholderStyles} />}
        <A2APolicyAlerts />
        {a2aDomainStatus === A2A_DOMAIN_STATUS.allowed && <A2APermission />}
        {a2aDomainStatus === A2A_DOMAIN_STATUS.allowed && <LegalDisclaimer />}
      </Stack>
    </Box>
  );
};

const A2A: FunctionComponent = () => (
  <A2AContextProvider>
    <A2AContent />
  </A2AContextProvider>
);

export default A2A;
